import React, { useState, useRef, useEffect } from 'react';
import Modal, { ButtonsLayout } from 'components/general/modal/modal';
import Select from 'components/form/select/select';
import TextInput from 'components/form/text/text';
import { modalSelectProps } from 'components/registration/modal_select_config';
const styles = require('./cancel_subscription_modal.module.scss');

const modalSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

interface Props {
  onClose?: () => void;
  cancelSubscriptionUrl: string;
  subscriptionPlan?: {
    in_trial?: boolean;
    subscription_product?: {
      with_duration_contract?: boolean;
    };
    end_date?: string;
  };
  isOpen?: boolean;
  cancellationReasons?: Array<{ value: string; label: string }>;
}

export default function CancelSubscriptionModal(props: Props) {
  const modalRef = useRef<Modal>(null);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [cancellationComments, setCancellationComments] = useState<string>('');
  const [errors, setErrors] = useState<{ cancellationReason?: string; cancellationComments?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectKey, setSelectKey] = useState(0);
  const [showSelect, setShowSelect] = useState(false);

  // Fallback to hardcoded reasons if not provided (for backwards compatibility)
  const cancellationReasons = props.cancellationReasons || [
    { value: 'Pricing', label: 'Pricing' },
    { value: 'Using internal resources', label: 'Using internal resources' },
    { value: 'No longer needed', label: 'No longer needed' },
    { value: 'We are not satisfied with the quality of the service', label: 'We are not satisfied with the quality of the service' },
    { value: 'We didn\'t get the business results we expected from the service', label: 'We didn\'t get the business results we expected from the service' },
    { value: 'Other', label: 'Other' },
  ];

  useEffect(() => {
    if (props.isOpen) {
      setShowSelect(false);
      setSelectKey(prev => prev + 1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setShowSelect(true);
          }, 50);
        });
      });
    } else {
      setShowSelect(false);
    }
  }, [props.isOpen]);

  const handleClose = () => {
    setCancellationReason('');
    setCancellationComments('');
    setErrors({});
    setIsSubmitting(false);
    if (props.onClose) {
      props.onClose();
    }
  };

  const handleReasonChange = (selectedOption: { value: string; label: string } | null) => {
    setCancellationReason(selectedOption ? selectedOption.value : '');
    if (errors.cancellationReason || errors.cancellationComments) {
      setErrors({});
    }
  };

  const handleCommentsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCancellationComments(event.target.value);
    if (errors.cancellationComments) {
      setErrors({ ...errors, cancellationComments: undefined });
    }
  };

  const renderSubscriptionInfo = () => {
    const { subscriptionPlan } = props;
    
    if (!subscriptionPlan) {
      return null;
    }

    if (subscriptionPlan.in_trial) {
      return (
        <div className={`ld-alert ld-form-alert ld-warning-alert ${styles.alertBox}`}>
          <p>
            Warning: Your trial will be cancelled immediately and you will not be charged.
          </p>
        </div>
      );
    }

    if (subscriptionPlan.subscription_product?.with_duration_contract && subscriptionPlan.end_date) {
      return (
        <div className={`ld-alert ld-form-alert ld-info-alert ${styles.alertBox}`}>
          <p>
            Your subscription will remain active until the end of your commitment period.
            You will continue to be charged monthly until then.
          </p>
        </div>
      );
    }

    return (
      <div className={`ld-alert ld-form-alert ld-info-alert ${styles.alertBox}`}>
        <p>
          Your subscription will remain active until the end of your current billing period.
        </p>
      </div>
    );
  };

  const handleCancelSubscription = () => {
    if (!cancellationReason) {
      setErrors({ cancellationReason: 'Please select a cancellation reason' });
      return;
    }

    const isOtherSelected = cancellationReason === 'Other';
    if (isOtherSelected && !cancellationComments.trim()) {
      setErrors({ cancellationComments: 'Please provide additional comments' });
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = props.cancelSubscriptionUrl;

    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'PATCH';
    form.appendChild(methodInput);

    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'authenticity_token';
    tokenInput.value = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    form.appendChild(tokenInput);

    const reasonInput = document.createElement('input');
    reasonInput.type = 'hidden';
    reasonInput.name = 'subscription_plan[cancellation_reason]';
    reasonInput.value = cancellationReason;
    form.appendChild(reasonInput);

    if (cancellationComments.trim() || cancellationReason === 'Other') {
      const commentsInput = document.createElement('input');
      commentsInput.type = 'hidden';
      commentsInput.name = 'subscription_plan[cancellation_comments]';
      commentsInput.value = cancellationComments;
      form.appendChild(commentsInput);
    }

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <Modal
      className={styles.modal}
      open={props.isOpen ?? false}
      ref={modalRef}
      title="Cancel Subscription"
      buttonsLayout={ButtonsLayout.CancelOk}
      cancelButton={true}
      cancelButtonTitle="Back"
      buttonName={isSubmitting ? 'Processing...' : 'Cancel Subscription'}
      disableButton={isSubmitting}
      buttonOnClick={handleCancelSubscription}
      onClose={handleClose}
      buttonColor="flamingo"
    >
      <div className={styles.formContainer}>
        {renderSubscriptionInfo()}
        <div className={styles.formGroup}>
          {showSelect && (
            <Select
              key={selectKey}
              label="Reason for cancellation"
              name="cancellation_reason"
              placeholder="Select a reason"
              options={cancellationReasons}
              value={
                cancellationReason
                  ? cancellationReasons.find(r => r.value === cancellationReason)
                  : null
              }
              onChange={handleReasonChange}
              error={errors.cancellationReason}
              color="tuftsBlue"
              required={true}
              styles={modalSelectStyles}
              menuShouldScrollIntoView={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              maxMenuHeight={200}
            />
          )}
        </div>
        <div className={styles.formGroup}>
          <TextInput
            label={cancellationReason === 'Other' ? 'Additional comments' : 'Additional comments (optional)'}
            name="cancellation_comments"
            placeholder="Please provide any additional feedback..."
            value={cancellationComments}
            onChange={handleCommentsChange}
            textarea={true}
            rows={4}
            color="tuftsBlue"
            required={cancellationReason === 'Other'}
            error={errors.cancellationComments}
          />
        </div>
      </div>
    </Modal>
  );
}

